import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { FormContent, Heading } from '@dreryk/react-components';

import { DiagnosesWithBodySideInteractiveTable } from '../../shared/DiagnosesWithBodySideInteractiveTable';
import { StyledReferralForm } from '../../shared/StyledReferralForm';
import { UnitNameAndAddressTextarea } from '../../shared/UnitNameAndAddressTextarea';
import { referralTranslationKeys } from '../../translations';
import { getInitialValues } from '../ReferralToPsychiatricHospitalSendAction/ReferralToPsychiatricHospitalSendAction.utils';

import { GuardianSection } from './fields/GuardianSection';
import { InformationAboutPatientSelect } from './fields/InformationAboutPatientSelect';
import { OtherJustifyingInformationTextarea } from './fields/OtherJustifyingInformation/OtherJustifyingInformationTextarea';
import { PatientConsentRadioGroup } from './fields/PatientConsentRadioGroup';
import { StatutoryRepresentativeConsentRadioGroup } from './fields/StatutoryRepresentativeConsentRadioGroup';
import {
   ReferralToPsychiatricHospitalFormBean,
   ReferralToPsychiatricHospitalFormController,
} from './referralToPsychiatricHospitalForm.types';

type ReferralToPsychiatricHospitalFormChildrenProps = Pick<
   ReferralToPsychiatricHospitalFormController,
   'handleSubmit' | 'handleSubmitDraft' | 'formState' | 'reset'
>;
type ReferralToPsychiatricFormProps = {
   readonly referralToPsychiatricHospitalDraft: ReferralToPsychiatricHospitalFormBean | null;
   readonly visitDiagnoses: VisitDiagnosis[] | undefined;
   readonly visitBeginDate: Date;
   readonly patientId: number;
   readonly children: (childrenProps: ReferralToPsychiatricHospitalFormChildrenProps) => JSX.Element;
};

export const ReferralToPsychiatricHospitalForm = (props: ReferralToPsychiatricFormProps): JSX.Element => {
   const {
      register,
      formState,
      handleSubmit,
      handleSubmitDraft,
      reset,
      watch,
      setValue,
      trigger,
      control,
   } = useFormController<ReferralToPsychiatricHospitalFormBean>(
      getInitialValues(props.referralToPsychiatricHospitalDraft, props.visitDiagnoses)
   );

   return (
      <>
         <Heading
            type={'h1'}
            testId={'referralToPsychiatricHospital'}
            content={referralTranslationKeys.referralType.PSYCHIATRIC_HOSPITAL}
         />
         <StyledReferralForm onSubmit={(event) => event.preventDefault()} noValidate>
            <FormContent testId={'referralToPsychiatricHospital'}>
               <UnitNameAndAddressTextarea register={register} />
               <PatientConsentRadioGroup register={register} />
               <StatutoryRepresentativeConsentRadioGroup register={register} />
               <InformationAboutPatientSelect register={register} />
               <GuardianSection
                  testId={'actualGuardian'}
                  name={'actualGuardian'}
                  patientId={props.patientId}
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  trigger={trigger}
               />
               <GuardianSection
                  testId={'statutoryRepresentative'}
                  name={'statutoryRepresentative'}
                  patientId={props.patientId}
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  trigger={trigger}
               />
               <DiagnosesWithBodySideInteractiveTable
                  register={register}
                  visitDiagnoses={props.visitDiagnoses}
                  dictionaryValidOn={props.visitBeginDate}
                  required
               />
               <OtherJustifyingInformationTextarea register={register} control={control} />
               {props.children({
                  handleSubmit: handleSubmit,
                  handleSubmitDraft: handleSubmitDraft,
                  formState: formState,
                  reset: reset,
               })}
            </FormContent>
         </StyledReferralForm>
      </>
   );
};
